import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import FunProgress from '../ui/FunProgress';
import { FileAudio, Scissors, Volume2, Zap, Download, AlertCircle, CheckCircle, Music, Clock, Radio } from 'lucide-react';

const SpeedLabel: Record<string, string> = {
  '0.50': 'Very Slow', '0.75': 'Slow', '1.00': 'Normal',
  '1.25': 'Fast', '1.50': 'Faster', '1.75': 'Very Fast', '2.00': 'Max',
};

const AUDIO_FORMATS = ['mp3', 'wav', 'aac', 'ogg', 'flac'] as const;

const AUDIO_PRESETS = [
  { id: 'podcast', label: 'Podcast',    sublabel: 'MP3·128k', format: 'mp3',  volume: '1.0', speed: '1.0' },
  { id: 'music',   label: 'Music',      sublabel: 'FLAC·loss',format: 'flac', volume: '1.0', speed: '1.0' },
  { id: 'voice',   label: 'Voice Memo', sublabel: 'AAC·64k',  format: 'aac',  volume: '1.2', speed: '1.0' },
  { id: 'story',   label: 'IG Story',   sublabel: 'AAC·128k', format: 'aac',  volume: '1.0', speed: '1.0' },
];

function formatBytes(b: number) {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

const AudioTools = () => {
  const [audioFile,  setAudioFile]  = useState<File | null>(null);
  const [dragging,   setDragging]   = useState(false);
  const [startTime,  setStartTime]  = useState('00:00:00');
  const [endTime,    setEndTime]    = useState('');
  const [volume,     setVolume]     = useState('1.0');
  const [speed,      setSpeed]      = useState('1.0');
  const [format,     setFormat]     = useState('mp3');
  const [processing, setProcessing] = useState(false);
  const [progress,   setProgress]   = useState(0);
  const [progStatus, setProgStatus] = useState('');
  const [error,      setError]      = useState<string | null>(null);
  const [success,    setSuccess]    = useState<string | null>(null);
  const [downloadUrl,setDownloadUrl]= useState<string | null>(null);

  const reset = () => { setError(null); setSuccess(null); setDownloadUrl(null); setProgress(0); setProgStatus(''); };

  const handleProcess = async () => {
    if (!audioFile) return setError('Please upload an audio file first');
    reset(); setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('file', audioFile);
      fd.append('startTime', startTime);
      fd.append('endTime', endTime || '');
      fd.append('volume', volume);
      fd.append('speed', speed);
      fd.append('format', format);
      const resp = await fetch('/api/convert/audio/process/stream', { method: 'POST', body: fd });
      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('Failed to start');
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        for (const line of decoder.decode(value).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === 'progress') { setProgress(d.percent); setProgStatus(d.status); }
            else if (d.type === 'complete') { setSuccess('Done!'); setDownloadUrl(`${d.downloadUrl}`); setProcessing(false); }
            else if (d.type === 'error') { setError(d.error); setProcessing(false); }
          } catch { /* ignore */ }
        }
      }
    } catch (e: any) { setError(e.message || 'Failed'); setProcessing(false); }
  };

  const speedLabel = SpeedLabel[parseFloat(speed).toFixed(2)] || `${speed}x`;

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="tool-hero">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-[#216974] to-[#54c4b6] flex items-center justify-center shadow-lg shadow-[#54c4b6]/30">
            <Music size={14} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 px-2.5 py-1 rounded-full">Audio Pro</span>
        </div>
        <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Professional Audio Tools</h1>
        <p className="text-xs text-gray-500 mt-0.5">Trim, boost, speed-adjust, and convert audio.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT */}
        <div className="space-y-4">
          <Card title="Audio File">
            <div
              className={`drop-zone p-5 text-center ${dragging ? 'dragging' : ''} ${audioFile ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) { setAudioFile(f); reset(); } }}
              onClick={() => document.getElementById('audio-file')?.click()}
            >
              <input type="file" id="audio-file" accept="audio/*" className="hidden"
                onChange={e => { if (e.target.files) { setAudioFile(e.target.files[0]); reset(); } }} />
              <div className={`w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center ${audioFile ? 'bg-[#216974]' : 'bg-gray-100'}`}>
                <FileAudio size={20} className={audioFile ? 'text-[#216974]' : 'text-gray-400'} />
              </div>
              {audioFile ? (
                <div><p className="font-semibold text-gray-800 text-sm truncate">{audioFile.name}</p><p className="text-xs text-gray-400">{formatBytes(audioFile.size)}</p></div>
              ) : (
                <div><p className="font-semibold text-gray-700 text-sm">Drop audio or click to browse</p><p className="text-xs text-gray-400 mt-0.5">MP3, WAV, AAC, M4A, FLAC, OGG</p></div>
              )}
            </div>
          </Card>

          {/* Presets */}
          <Card title="Quick Presets">
            <div className="grid grid-cols-4 gap-1.5">
              {AUDIO_PRESETS.map(p => (
                <button key={p.id} onClick={() => { setFormat(p.format); setVolume(p.volume); setSpeed(p.speed); }}
                  className={`preset-card py-2 ${format === p.format ? 'active' : ''}`}>
                  <Radio size={12} className="text-[#216974]" />
                  <span className="text-[10px] font-semibold text-gray-700">{p.label}</span>
                  <span className="text-[9px] text-gray-400">{p.sublabel}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* RIGHT */}
        <Card title="Adjustments">
          <div className="space-y-4">
            {/* Trim */}
            <div>
              <p className="section-label flex items-center gap-1 mb-2"><Scissors size={11} className="text-[#216974]" /> Trim</p>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Start" placeholder="00:00:00" value={startTime} onChange={e => setStartTime(e.target.value)} leftIcon={<Clock size={13} />} className="font-mono text-xs" />
                <Input label="End" placeholder="00:00:00" value={endTime} onChange={e => setEndTime(e.target.value)} leftIcon={<Clock size={13} />} className="font-mono text-xs" />
              </div>
            </div>

            {/* Volume */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="section-label flex items-center gap-1 mb-0"><Volume2 size={11} className="text-[#216974]" /> Volume</p>
                <span className="text-xs font-mono text-[#216974]">{volume}x</span>
              </div>
              <input type="range" min="0.1" max="3.0" step="0.1" value={volume} onChange={e => setVolume(e.target.value)} className="w-full h-1.5 accent-blue-500" />
              <div className="flex justify-between text-[9px] text-gray-400 mt-1"><span>Silent</span><span>Normal</span><span>3× Boost</span></div>
            </div>

            {/* Speed */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="section-label flex items-center gap-1 mb-0"><Zap size={11} className="text-[#216974]" /> Speed</p>
                <span className="text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 px-2 py-0.5 rounded-lg">{speedLabel}</span>
              </div>
              <input type="range" min="0.5" max="2.0" step="0.25" value={speed} onChange={e => setSpeed(e.target.value)} className="w-full h-1.5 accent-blue-500" />
              <div className="flex justify-between text-[9px] text-gray-400 mt-1"><span>0.5×</span><span>1×</span><span>2×</span></div>
            </div>

            {/* Format */}
            <div>
              <p className="section-label mb-1.5">Format</p>
              <div className="flex flex-wrap gap-1.5">
                {AUDIO_FORMATS.map(f => <button key={f} onClick={() => setFormat(f)} className={`format-chip ${format === f ? 'active' : ''}`}>{f}</button>)}
              </div>
            </div>

            <Button size="lg" className="w-full" isLoading={processing} disabled={!audioFile || processing} onClick={handleProcess} leftIcon={<Zap size={16} />}>
              {processing ? 'Processing…' : 'Apply & Convert'}
            </Button>
          </div>
        </Card>
      </div>

      {/* Progress + Alerts — always full width below the grid */}
      <AnimatePresence>
        {processing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <FunProgress percent={progress} tool="audio" />
            {progStatus && <p className="text-xs text-center text-gray-500 mt-1 animate-pulse">{progStatus}</p>}
          </motion.div>
        )}
        {error && <motion.div key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="alert-error"><AlertCircle size={14} className="shrink-0" /><p className="text-xs font-medium">{error}</p></motion.div>}
        {success && (
          <motion.div key="ok" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
            <div className="alert-success"><CheckCircle size={14} className="shrink-0" /><p className="text-xs font-medium">{success}</p></div>
            {downloadUrl && <a href={downloadUrl} download className="block"><Button size="sm" variant="success" className="w-full" leftIcon={<Download size={14} />}>Download Audio</Button></a>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AudioTools;
